import { apiClient } from '../config/api';

// FEAT-064 — Caregiver Service Areas (web client slice).
// MVP-18.33 — caregivers select the zip-code service areas they serve,
// gated behind an explicit confirmation agreement.

export interface ServiceArea {
  id: string;
  zip: string;
  city?: string | null;
  state?: string | null;
  regionId?: string | null;
}

export interface ServiceAreaAgreementStatus {
  accepted: boolean;
  version: string | null;
  acceptedAt: string | null;
}

export interface ServiceAreaStatusResponse {
  selected: ServiceArea[];
  available: ServiceArea[];
  agreement: ServiceAreaAgreementStatus;
  currentAgreementVersion: string;
}

export interface AcceptAgreementResponse {
  agreement: {
    accepted: true;
    version: string;
    acceptedAt: string;
  };
}

export interface UpdateServiceAreasResponse {
  selected: ServiceArea[];
}

class CaregiverServiceAreaService {
  /**
   * GET /caregiver/service-areas
   * Returns the caregiver's selected areas, all admin-active available areas,
   * and the agreement status.
   */
  async getServiceAreas(): Promise<ServiceAreaStatusResponse> {
    const { data } = await apiClient.get<ServiceAreaStatusResponse>(
      '/caregiver/service-areas',
    );
    return data;
  }

  /**
   * POST /caregiver/service-areas/agreement
   * Accept the service-area confirmation agreement at the given version.
   * 400 if the version is wrong.
   */
  async acceptAgreement(version: string): Promise<AcceptAgreementResponse> {
    const { data } = await apiClient.post<AcceptAgreementResponse>(
      '/caregiver/service-areas/agreement',
      { version },
    );
    return data;
  }

  /**
   * PUT /caregiver/service-areas
   * Replace the caregiver's selected service areas.
   * 409 AGREEMENT_REQUIRED if the agreement hasn't been accepted.
   * 400 INACTIVE_SERVICE_AREA if any id is inactive/unknown.
   */
  async updateServiceAreas(
    serviceAreaIds: string[],
  ): Promise<UpdateServiceAreasResponse> {
    const { data } = await apiClient.put<UpdateServiceAreasResponse>(
      '/caregiver/service-areas',
      { serviceAreaIds },
    );
    return data;
  }
}

export const caregiverServiceAreaService = new CaregiverServiceAreaService();
